import { useState } from "react";
import {
  FetchError,
  Header,
  NotificationBody as MessageBody,
} from "components/molecules";
import { AlertPageFilterButtonSection } from "components/organisms/AlertPageFilterButtonSection";
import { TAB_LARGE_WIDTH, MOBILE_WIDTH } from "utils/constants";
import { useScreenSize } from "hooks";
import { BackIcon, Spinner } from "components/atoms/Icon";
import { Typography } from "components/atoms";
import { useNavigate } from "react-router-dom";
import {
  useGetAllNotificationsQuery,
  useLazyGetUnreadNotificationsCountQuery,
  useMarkNotificationAsReadMutation,
} from "services/alerts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "./index.scss";

dayjs.extend(relativeTime);

interface IProps {
  theme: "light" | "dark";
}

interface SelectedBtnState {
  [key: string]: boolean;
  ViewAll: boolean;
  Coursework: boolean;
  Message: boolean;
  TestReminders: boolean;
  Surveys: boolean;
}

export const AlertPage = ({ theme }: IProps) => {
  const screen = useScreenSize();
  const navigate = useNavigate();

  const [selectedBtn, setSelectedBtn] = useState<SelectedBtnState>({
    ViewAll: true,
    Coursework: false,
    Message: false,
    TestReminders: false,
    Surveys: false,
    Discounts: false,
  });

  const {
    data,
    isLoading,
    isError,
    refetch: refetchGetAllNotifications,
  } = useGetAllNotificationsQuery();

  const [ triggerRefetchGetUnreadNotificationsCount ] =
    useLazyGetUnreadNotificationsCountQuery();

  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();

  const handleBack = () => {
    navigate(-1);
  };

  const handleNotificationClick = async (item: any) => {
    window.open(item.LaunchUrl, "__blank");
    try {
      await markNotificationAsRead({
        notificationGuid: item.NotificationGuid,
      }).then(() => {
        refetchGetAllNotifications();
        triggerRefetchGetUnreadNotificationsCount();
      });
    } catch (err: any) {
      console.log(err);
    }
  };

  return (
    <div className="alert-page-wrapper">
      {screen.width <= MOBILE_WIDTH ? (
        <div className="back-wrapper">
          <BackIcon
            color={theme === "light" ? "#233851" : "#329EE2"}
            onClickHandle={handleBack}
          />
          <Typography variant="h6Medium" label="Back" onClick={handleBack} />
        </div>
      ) : null}
      <div className="alert-header-container">
        <div className="header-section">
          <Header
            theme={theme}
            leftContentLabel="Alerts"
            leftContentStyle="header-custom-style"
            leftContentTypographyStyle="left-content-typography-style"
            isTab={screen.width <= TAB_LARGE_WIDTH}
            customVariant={screen.width <= MOBILE_WIDTH ? "h2Bold" : "h3Bold"}
            isAlert={true}
          />
        </div>
      </div>
      <div className="filter-btn-section">
        <AlertPageFilterButtonSection
          selectedBtn={selectedBtn}
          setSelectedBtn={setSelectedBtn}
        />
      </div>
      <div className="message-section">
        {isLoading ? (
          <Spinner theme={theme} />
        ) : isError ? (
          <FetchError content="Oops! failed to load resources." />
        ) : data && Array.isArray(data) && data.length === 0 ? (
          <Typography label="No data available" variant="h5Med" />
        ) : (
          <>
            {data
              .filter(
                (item: any) =>
                  selectedBtn["ViewAll"] || selectedBtn[item.Filter]
              )
              .map((item: any, index: number) => (
                <MessageBody
                  key={index}
                  title={item.Title}
                  text={item.Body}
                  labels={item.Labels}
                  isRead={Boolean(item.IsRead)}
                  time={dayjs(item.SendDateUtc).fromNow()}
                  onClick={() => handleNotificationClick(item)}
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
};
